import { useState } from 'react';
import { UseSessionStorageValue } from './UseSessionStorageValue';

// Storage can be unavailable (site data blocked, private modes, quota exceeded) and what is in
// it can be unreadable. Neither may take the app down: the value then lives in memory only.
function setSessionStorageState<TState>(key: string, value: TState): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Kept in memory by the hook; a reload starts over.
  }
}

function getSessionStorageState<TState>(key: string): TState | null {
  try {
    const sessionStorageValue = window.sessionStorage.getItem(key);

    return sessionStorageValue ? JSON.parse(sessionStorageValue) : null;
  } catch {
    return null;
  }
}

/**
 * State that survives a reload but ends with the tab. localStorage is deliberately not used:
 * it has no expiry and is shared by every tab and every later visitor on the device.
 */
export function useSessionStorage<TValue>(key: string): UseSessionStorageValue<TValue> {
  const [state, setState] = useState<TValue | null>(() => getSessionStorageState<TValue>(key));

  const setValue = (value: TValue): void => {
    setState(value);
    setSessionStorageState(key, value);
  };

  return [state, setValue];
}
